import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { brandClass, brandStyle } from "@/lib/brandTheme";
import { LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DemoBanner } from "@/components/DemoBanner";
import { useAuth } from "@/context/AuthContext";

const NavItem = ({ item, compact }) => (
  <NavLink
    to={item.to}
    end={item.end}
    data-testid={`nav-${item.id}`}
    className={({ isActive }) =>
      compact
        ? `flex min-w-[64px] flex-col items-center gap-1 px-2 py-2 text-[11px] font-semibold transition-colors ${isActive ? "text-indigo-600" : "text-slate-500"}`
        : `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`
    }
  >
    <item.icon className={compact ? "h-5 w-5" : "h-4 w-4"} />
    {item.label}
  </NavLink>
);

export default function AppShell({ nav, title, branded = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const biz = user?.business;
  const colors = branded ? biz?.brand_colors : null;
  const logoUrl = branded && biz?.brand_version && biz?.slug ? `${process.env.REACT_APP_BACKEND_URL}/api/public/brand/${biz.slug}/logo.png?v=${biz.brand_version}` : null;
  const brandLogo = logoUrl ? <img src={logoUrl} alt={biz.name} className="max-h-10 max-w-[160px] object-contain" data-testid="shell-brand-logo" /> : <Logo to={nav[0].to} />;
  useEffect(() => {
    document.title = `${title} | ${biz?.name || "StaffRotas"}`;
  }, [title, biz?.name]);
  useEffect(() => {
    const meta = document.querySelector("meta[name=theme-color]");
    if (meta && colors?.primary) meta.setAttribute("content", colors.primary);
    return () => meta?.setAttribute("content", "#4f46e5");
  }, [colors?.primary]);
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className={`flex min-h-screen bg-slate-50 ${brandClass(colors)}`} style={brandStyle(colors)} data-testid="app-shell">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-5 lg:flex">
        {brandLogo}
        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
        <nav className="mt-3 space-y-1">{nav.map((i) => <NavItem key={i.id} item={i} />)}</nav>
        <div className="mt-auto border-t border-slate-100 pt-4">
          <p className="truncate text-sm font-semibold text-slate-900" data-testid="shell-user-name">{user?.name}</p>
          <p className="truncate text-xs text-slate-500">{user?.business?.name || "Platform owner"}</p>
          <button onClick={doLogout} className="mt-3 flex items-center gap-2 text-sm text-slate-500 hover:text-rose-600" data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <DemoBanner />
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl lg:hidden">
          {brandLogo}
          <button onClick={doLogout} className="text-slate-500" data-testid="logout-btn-mobile"><LogOut className="h-5 w-5" /></button>
        </header>
        <main className="flex-1 px-4 pb-28 pt-6 sm:px-8 lg:px-10 lg:pb-10">
          <Outlet />
        </main>
        <nav className="fixed inset-x-0 bottom-0 z-40 flex justify-around overflow-x-auto border-t border-slate-200 bg-white/95 backdrop-blur lg:hidden">
          {nav.map((i) => <NavItem key={i.id} item={i} compact />)}
        </nav>
      </div>
    </div>
  );
}
